// Echtzeit-Verbindung zu WS /api/ws (Fundament). Eine Verbindung je Fenster, automatisch neu verbinden.
//
//   const weg = echtzeit.abonnieren((e) => { if (e.type === "activity.created" && e.lead_id === leadId) neuLaden(); });
//   …  weg();   // abmelden
//
// In React: useEchtzeit(["activity.created"], (e) => …).
import { useEffect, useRef } from "react";
import type { Ereignis, EreignisTyp } from "./types";

type Hoerer = (e: Ereignis) => void;
type Zustand = "getrennt" | "verbindet" | "verbunden";

class EchtzeitVerbindung {
  private ws: WebSocket | null = null;
  private hoerer = new Set<Hoerer>();
  private zustandHoerer = new Set<(z: Zustand) => void>();
  private ping: number | undefined;
  private warte = 1000;
  private aktiv = false;
  private zeitgeber: number | undefined;
  zustand: Zustand = "getrennt";

  starten(): void {
    if (this.aktiv) return;
    this.aktiv = true;
    this.verbinden();
  }

  stoppen(): void {
    this.aktiv = false;
    window.clearTimeout(this.zeitgeber);
    window.clearInterval(this.ping);
    this.ws?.close();
    this.ws = null;
    this.setzeZustand("getrennt");
  }

  abonnieren(h: Hoerer): () => void {
    this.hoerer.add(h);
    return () => this.hoerer.delete(h);
  }

  zustandAbonnieren(h: (z: Zustand) => void): () => void {
    this.zustandHoerer.add(h);
    return () => this.zustandHoerer.delete(h);
  }

  private setzeZustand(z: Zustand): void {
    this.zustand = z;
    this.zustandHoerer.forEach((h) => h(z));
  }

  private verbinden(): void {
    if (!this.aktiv) return;
    const protokoll = window.location.protocol === "https:" ? "wss:" : "ws:";
    this.setzeZustand("verbindet");
    const ws = new WebSocket(`${protokoll}//${window.location.host}/api/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.warte = 1000;
      this.setzeZustand("verbunden");
      window.clearInterval(this.ping);
      this.ping = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
      }, 30_000);
    };
    ws.onmessage = (nachricht) => {
      try {
        const e = JSON.parse(nachricht.data as string) as Ereignis;
        this.hoerer.forEach((h) => h(e));
      } catch {
        /* ungültige Nachricht ignorieren */
      }
    };
    ws.onclose = () => {
      window.clearInterval(this.ping);
      if (this.ws === ws) this.ws = null;
      this.setzeZustand("getrennt");
      if (this.aktiv) {
        this.zeitgeber = window.setTimeout(() => this.verbinden(), this.warte);
        this.warte = Math.min(this.warte * 2, 30_000);
      }
    };
  }
}

export const echtzeit = new EchtzeitVerbindung();

/** Ereignisse der angegebenen Typen empfangen, solange die Komponente sichtbar ist. */
export function useEchtzeit(typen: EreignisTyp[] | "alle", handler: (e: Ereignis) => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  const schluessel = typen === "alle" ? "alle" : typen.join(",");
  useEffect(() => {
    const erlaubt = typen === "alle" ? null : new Set(typen);
    return echtzeit.abonnieren((e) => {
      if (!erlaubt || erlaubt.has(e.type)) ref.current(e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);
}
